import { useNavigate } from "react-router-dom";
import { useTranslation } from "../i18n";
import { T } from "../tokens";

const InstagramIcon = ({ size = 24, ...props }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
    <rect x="2" y="2" width="20" height="20" rx="5" ry="5"></rect>
    <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path>
    <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line>
  </svg>
);

const FacebookIcon = ({ size = 24, ...props }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
    <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"></path>
  </svg>
);

const YoutubeIcon = ({ size = 24, ...props }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
    <path d="M22.54 6.42a2.78 2.78 0 0 0-1.94-2C18.88 4 12 4 12 4s-6.88 0-8.6.46a2.78 2.78 0 0 0-1.94 2A29 29 0 0 0 1 11.75a29 29 0 0 0 .46 5.33A2.78 2.78 0 0 0 3.4 19c1.72.46 8.6.46 8.6.46s6.88 0 8.6-.46a2.78 2.78 0 0 0 1.94-2 29 29 0 0 0 .46-5.25 29 29 0 0 0-.46-5.33z"></path>
    <polygon points="9.75 15.02 15.5 11.75 9.75 8.48 9.75 15.02"></polygon>
  </svg>
);

const WhatsappIcon = ({ size = 24, ...props }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
    <g transform="translate(2.4, 2.4) scale(0.8)">
      <path d="M17.49 15.3c-.3.15-1.78-.88-2.05-.97-.27-.1-.47-.15-.67.15-.2.3-.77.97-.94 1.17-.17.2-.35.22-.65.07a9.73 9.73 0 0 1-2.42-1.5 10.83 10.83 0 0 1-1.67-2.08c-.18-.3-.02-.47.13-.62.14-.13.3-.35.45-.53.15-.17.2-.3.3-.5.1-.2.05-.38-.02-.53-.07-.15-.67-1.62-.92-2.22-.24-.57-.49-.5-.67-.5-.18 0-.38-.02-.58-.02a1.12 1.12 0 0 0-.82.38 3.4 3.4 0 0 0-1.06 2.53c0 1.5 1.08 2.93 1.23 3.13.15.2 2.13 3.25 5.16 4.56.72.3 1.28.5 1.72.64.73.23 1.4.2 1.92.12.58-.08 1.78-.73 2.03-1.43.25-.7.25-1.3.17-1.43-.07-.13-.27-.2-.58-.35z"></path>
    </g>
    <path d="M22 12A10 10 0 0 0 12 2v0a10 10 0 0 0-8.6 15L2 22l5.1-1.3A10 10 0 0 0 22 12z"></path>
  </svg>
);

const SOCIAL = [
  { icon: InstagramIcon, label: "Instagram", href: "https://instagram.com/fayriza" },
  { icon: FacebookIcon,  label: "Facebook",  href: "https://facebook.com/fayriza"  },
  { icon: YoutubeIcon,   label: "YouTube",   href: "https://youtube.com/@fayriza"  },
  { icon: WhatsappIcon,  label: "WhatsApp",  href: "https://wa.me/919876543210"    },
];

export default function Footer() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const go = (path) => (e) => {
    e.preventDefault();
    navigate(path);
  };

  const COLS = [
    {
      title: t("footer.services"),
      links: [
        { l: t("footer.salons"),     path: "/salons" },
        { l: t("nav.ai_advisor"),    path: "/ai" },
        { l: t("footer.shop"),       path: "/shop" },
        { l: t("footer.academy"),    path: "/academy" },
        { l: t("footer.membership"), path: "/membership" },
        { l: t("loyalty.rewards"),   path: "/loyalty" },
      ],
    },
    {
      title: t("footer.company"),
      links: [
        { l: t("footer.about"),        path: "/about" },
        { l: t("footer.how_it_works"), path: "/how-it-works" },
        { l: t("footer.partners"),     path: "/partners" },
        { l: t("footer.stylists"),     path: "/stylists" },
        { l: t("footer.careers"),      path: "/careers" },
        { l: t("footer.contact"),      path: "/contact" },
      ],
    },
    {
      title: t("footer.support"),
      links: [
        { l: t("footer.faq"),     path: "/faq" },
        { l: t("footer.refund"),  path: "/refund-policy" },
        { l: t("footer.privacy"), path: "/privacy" },
        { l: t("footer.terms"),   path: "/terms" },
      ],
    },
  ];

  return (
    <footer
      style={{
        background: T.dark2,
        borderTop: `1px solid ${T.goldBdr}`,
        padding: "64px 48px 32px",
        fontFamily: "'DM Sans',sans-serif",
      }}
    >
      <div
        style={{
          maxWidth: 1240,
          margin: "0 auto",
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
          gap: 40,
          marginBottom: 44,
        }}
      >
        <div>
          <button
            type="button"
            onClick={() => navigate("/")}
            aria-label="Fayriza — home"
            style={{
              background: "transparent",
              border: "none",
              padding: 0,
              fontFamily: "Cormorant Garamond,serif",
              fontSize: 34,
              color: T.gold,
              fontStyle: "italic",
              marginBottom: 14,
              cursor: "pointer",
            }}
          >
            Fayriza
          </button>
          <p style={{ fontSize: 13, color: T.muted, lineHeight: 1.8, maxWidth: 290, marginBottom: 22 }}>
            {t("footer.tagline")}
          </p>
          <div style={{ display: "flex", gap: 10 }}>
            {SOCIAL.map((s) => {
              const Icon = s.icon;
              return (
                <a
                  key={s.label}
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={s.label}
                  style={{
                    width: 34,
                    height: 34,
                    borderRadius: "50%",
                    background: T.dark3,
                    border: `1px solid ${T.goldBdr}`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    color: T.muted,
                    textDecoration: "none",
                    transition: "all .2s",
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.borderColor = T.gold;
                    e.currentTarget.style.background = T.goldDim;
                    e.currentTarget.style.color = T.goldLt;
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.borderColor = T.goldBdr;
                    e.currentTarget.style.background = T.dark3;
                    e.currentTarget.style.color = T.muted;
                  }}
                >
                  <Icon size={16} strokeWidth={2} />
                </a>
              );
            })}
          </div>
        </div>
        {COLS.map((col) => (
          <nav key={col.title} aria-label={col.title}>
            <div
              style={{
                fontSize: 11,
                color: T.gold,
                letterSpacing: ".15em",
                textTransform: "uppercase",
                marginBottom: 18,
              }}
            >
              {col.title}
            </div>
            <ul style={{ listStyle: "none", display: "flex", flexDirection: "column", gap: 10, padding: 0 }}>
              {col.links.map((lnk) => (
                <li key={lnk.l}>
                  <a
                    href={lnk.path}
                    onClick={go(lnk.path)}
                    style={{
                      fontSize: 13.5,
                      color: T.muted,
                      cursor: "pointer",
                      textDecoration: "none",
                    }}
                    onMouseOver={(e) => (e.currentTarget.style.color = T.text)}
                    onMouseOut={(e) => (e.currentTarget.style.color = T.muted)}
                  >
                    {lnk.l}
                  </a>
                </li>
              ))}
            </ul>
          </nav>
        ))}
      </div>

      {/* Gold upsell */}
      <div
        style={{
          maxWidth: 1240,
          margin: "0 auto",
          background: "linear-gradient(120deg,#2A1C08,#1C1208,#2A1C08)",
          border: `1px solid ${T.goldBdrS}`,
          borderRadius: 14,
          padding: "20px 28px",
          marginBottom: 32,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          position: "relative",
          overflow: "hidden",
          gap: 14,
          flexWrap: "wrap",
        }}
      >
        <div
          aria-hidden="true"
          style={{
            position: "absolute",
            right: -20,
            top: -20,
            width: 160,
            height: 160,
            borderRadius: "50%",
            background:
              "radial-gradient(circle,rgba(201,168,76,.1) 0%,transparent 70%)",
            pointerEvents: "none",
          }}
        />
        <div>
          <div style={{ fontFamily: "Cormorant Garamond,serif", fontSize: 20, color: T.text, marginBottom: 4 }}>
            {t("home.cta_title")} <span style={{ color: T.goldLt, fontWeight: 600 }}>20% OFF</span> · Fayriza Gold
          </div>
          <div style={{ fontSize: 12.5, color: T.muted }}>
            {t("home.cta_sub")}
          </div>
        </div>
        <button
          type="button"
          onClick={() => navigate("/membership")}
          style={{
            padding: "11px 24px",
            borderRadius: 10,
            flexShrink: 0,
            background: `linear-gradient(135deg,${T.goldLt},${T.gold})`,
            border: "none",
            color: T.dark,
            fontSize: 13,
            fontWeight: 600,
            cursor: "pointer",
            fontFamily: "inherit",
          }}
        >
          {t("membership.get_plan", { plan: t("membership.gold") })} →
        </button>
      </div>

      <div
        style={{
          maxWidth: 1240,
          margin: "0 auto",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          paddingTop: 24,
          borderTop: `1px solid ${T.goldBdr}`,
          fontSize: 12,
          color: T.subtle,
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        <span>{t("footer.copyright", { year: new Date().getFullYear() })} · {t("footer.made_in")}</span>
        <div style={{ display: "flex", gap: 16 }}>
          {[
            { l: t("footer.privacy"), path: "/privacy" },
            { l: t("footer.terms"),   path: "/terms" },
            { l: t("footer.refund"),  path: "/refund-policy" },
          ].map((l) => (
            <a
              key={l.l}
              href={l.path}
              onClick={go(l.path)}
              style={{ cursor: "pointer", color: T.subtle, textDecoration: "none" }}
              onMouseOver={(e) => (e.currentTarget.style.color = T.muted)}
              onMouseOut={(e) => (e.currentTarget.style.color = T.subtle)}
            >
              {l.l}
            </a>
          ))}
        </div>
      </div>
    </footer>
  );
}
